import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { file_path } from "./environment";
import axios from "axios";

import EditorialSearch from './EditorialSearch.js';

function Feedback({ item }) {
    const contentItems = Array.isArray(item.content)
        ? item.content
        : item.content ? [item.content] : [];

    return (
        <div className="feedback-item">
            <h4>{item.title}</h4>
            {contentItems.map((content, index) => (
                <div key={`${item.title}-${index}`} dangerouslySetInnerHTML={{ __html: content }} />
            ))}
        </div>
    );
}

function getLatestYear(history) {
    const years = Object.keys(history || {}).sort((yearA, yearB) => Number(yearB) - Number(yearA));
    return years[0] || null;
}

function History({ history }) {
    if (!history) {
        return null;
    }

    return (
        <div className="firm-history-list">
            {Object.entries(history)
                .sort(([yearA], [yearB]) => Number(yearB) - Number(yearA))
                .map(([year, data]) => (
                <section key={year} className="firm-year-card">
                    <div className="firm-year-header">
                        <h3>{year}</h3>
                        <span>{data.ranking}</span>
                    </div>
                    <div className="firm-year-body">
                        {data.feedback.map((feedback) => {
                            return <Feedback key={feedback.title} item={feedback} />;
                        })}
                    </div>
                </section>
            ))}
        </div>
    );
}

function getGuideCounts(subsections) {
    return Object.values(subsections || {}).reduce((counts, subsection) => {
        const guide = subsection.guide || "Unknown";
        counts[guide] = (counts[guide] || 0) + 1;
        return counts;
    }, {});
}

function filterSubsectionsByGuides(subsections, selectedGuides) {
    return Object.entries(subsections || {}).reduce((filtered, [id, subsection]) => {
        if (selectedGuides.includes(subsection.guide)) {
            filtered[id] = subsection;
        }
        return filtered;
    }, {});
}

function GuideFilter({ guideCounts, selectedGuides, toggleGuide, clearAll }) {
    if (!Object.keys(guideCounts).length) {
        return null;
    }

    return (
        <section className="guide-filter">
            <p className="guide-filter-label">Filter by guide</p>
            <div className="guide-filter-options">
                {Object.entries(guideCounts).map(([guide, count]) => (
                    <label key={guide} className="guide-filter-option">
                        <input
                            type="checkbox"
                            checked={selectedGuides.includes(guide)}
                            onChange={() => toggleGuide(guide)}
                        />
                        {guide} ({count})
                    </label>
                ))}
            </div>
            <div className="guide-filter-footer">
                <button type="button" className="guide-filter-clear" onClick={clearAll}>
                    Uncheck all
                </button>
            </div>
        </section>
    );
}

function Subsection({ subsection }) {
    const latestYear = getLatestYear(subsection.history);

    return (
        <section className="firm-section-card">
            <div className="firm-section-header">
                <div>
                    <p className="firm-section-kicker">{subsection.guide} Guide</p>
                    <h2>{subsection.practise}</h2>
                    <p className="firm-section-location">{subsection.location}</p>
                </div>
                <div className="firm-section-meta">
                    <div>
                        <span className="firm-meta-label">Years ranked</span>
                        <span className="firm-meta-value">{subsection.yearsRanked}</span>
                    </div>
                    {latestYear ? (
                        <div>
                            <span className="firm-meta-label">Latest year</span>
                            <span className="firm-meta-value">{latestYear}</span>
                        </div>
                    ) : null}
                </div>
            </div>
            <History history={subsection.history} />
        </section>
    );
}

function Content({ subsections }) {
    if (!subsections) {
        return null;
    }

    return (
        <div>
            {Object.entries(subsections).map(([id, subsection]) => (
                <Subsection key={id} subsection={subsection} />
            ))}
        </div>
    );
}

export default function EditorialLawyers() {
    const [lawyer, setLawyer] = useState({});
    const [selectedGuides, setSelectedGuides] = useState([]);
    const [showFilters, setShowFilters] = useState(false);
    const { lawyerId } = useParams();

    const sectionCount = Object.keys(lawyer.subsections || {}).length;
    const guideCounts = getGuideCounts(lawyer.subsections);
    const filteredSubsections = filterSubsectionsByGuides(lawyer.subsections, selectedGuides);

    useEffect(() => {
        axios.get(file_path(`/lawyer-page/${lawyerId}.json`)).then((res) => {
            setLawyer(res.data);
            document.title = `${res.data.name} - ${res.data.firm}`;
            setSelectedGuides(Object.keys(getGuideCounts(res.data.subsections)));
        });
    }, [lawyerId]);

    function toggleGuide(guide) {
        setSelectedGuides((current) =>
            current.includes(guide)
                ? current.filter((item) => item !== guide)
                : [...current, guide]
        );
    }

    function clearAll() {
        setSelectedGuides([]);
    }

    return (
        <main className="firm-editorial-page">

            <EditorialSearch />

            <section className="hero lawyer-hero">
                <div className="portal-links-row portal-links-row--right">
                    <a className="guides-return-link report-request-link" href="mailto:mbuckley@gmail.com?subject=Portal%20Bug%20or%20Feature%20Request">Report a bug or request a feature</a>
                </div>
                <h1>{lawyer.name}</h1>
                <div className="firm-hero__stats">
                    <div className="firm-hero__stat">
                        <span className="firm-meta-label">Firm</span>
                        <span className="firm-meta-value">
                            <Link to={`/editorial/firms/${lawyer.firm_id}`} role="button">{lawyer.firm}</Link>
                        </span>
                    </div>
                    <div className="firm-hero__stat">
                        <span className="firm-meta-label">Practice areas</span>
                        <span className="firm-meta-value">{sectionCount}</span>
                    </div>
                </div>
            </section>

            <div className="guide-filter-toggle">
                <button type="button" className="guide-filter-toggle-button" onClick={() => setShowFilters((show) => !show)}>
                    {showFilters ? "Hide guide filters" : "Show guide filters"}
                </button>
            </div>
            {showFilters ? (
                <GuideFilter
                    guideCounts={guideCounts}
                    selectedGuides={selectedGuides}
                    toggleGuide={toggleGuide}
                    clearAll={clearAll}
                />
            ) : null}

            <Content subsections={filteredSubsections} />
        </main>
    );
}